import React, {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { global } from "@shared/libs/Global";
import {
  getCurrentTime,
  getInitPopUp,
  getNoSlip,
} from "@shared/utils/CommonUtils";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import FreeForm from "@widgets/FreeForm";
import { menuInfo } from "@shared/type/CommonType";
import SnEditor from "@shared/components/SnEditor";
import { fnSave } from "@shared/api/restApi";
import { sendIssueSnapshotTelegramByParams } from "../issueTelegram";
import {
  getEmp,
  getEtcInfo,
  getProgress,
  getRelIssue,
  getTag,
  getTxtBody,
} from "@shared/utils/IssueUtils";
import { showDialog } from "@widgets/CommonDialog";
import CommonSpinner from "@widgets/CommonSpinner";

interface popupProps {
  modalRef?: any;
  Menuprops: menuInfo;
}

/**
 * NEW ISSUE 팝업
 */
const SN_SFA_ISSUE_REP_001_POP_001 = forwardRef(
  ({ modalRef, Menuprops }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const uniqueId = useId();

    const formRef = useRef<any>(null);
    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [body, setBody] = useState("");

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUp("SN_SFA_ISSUE_REP_001_POP_001");
          let formData = rtn.form.freeform1ControlData;

          // 오늘 기준으로 다다음주 월요일 계산
          const today = new Date();
          const day = today.getDay(); // 0 (일) ~ 6 (토)
          const diffToMonday = (day === 0 ? -6 : 1) - day;
          const thisWeekMonday = new Date(today);
          thisWeekMonday.setDate(today.getDate() + diffToMonday);

          const twoWeeksLaterMonday = new Date(thisWeekMonday);
          twoWeeksLaterMonday.setDate(thisWeekMonday.getDate() + 14);

          const yyyy = twoWeeksLaterMonday.getFullYear();
          const mm = String(twoWeeksLaterMonday.getMonth() + 1).padStart(
            2,
            "0"
          );
          const dd = String(twoWeeksLaterMonday.getDate()).padStart(2, "0");

          formData.dt_due = `${yyyy}${mm}${dd}`;

          setFormData(formData);
          setFormControlData(rtn.form.freeform1);
        } catch (error) {}
      };
      fetchform();
    }, []);

    useEffect(() => {
      if (!formData) return; // 아직 formData가 없다면 skip
      if (formData.no_issue) return; // 이미 채번 되어 있으면 skip

      const fetchform = async () => {
        try {
          const cd_module: any = Menuprops.cd_module;
          const cd_menu: any = Menuprops.cd_menu;
          const today = new Date();
          const formattedDate = today
            .toISOString()
            .slice(0, 10)
            .replace(/-/g, "");

          const no_issue = await getNoSlip(
            cd_module,
            1, // no_line 값이 필요한 경우 사용
            "ISS", // cd_prefix 값이 필요하다면 여기에 적어주세요.
            formattedDate, // 오늘 날짜(ex.20250425)
            cd_menu
          );

          const mergedData = { ...formData, ...{ no_issue: no_issue } };

          // 1. State 업데이트 (저장 로직에서 참조할 백업 데이터)
          setFormData(mergedData);

          // 2. Form Ref 업데이트 (UI 표시 및 에디터 활성화용)
          // 폼 라이브러리 특성에 따라 setData 혹은 addRow를 확실히 호출
          if (formRef.current) {
            formRef.current.setData?.(mergedData) ||
              formRef.current.addRow(mergedData);
          }
        } catch (error) {}
      };
      fetchform();
    }, [formData]);

    useImperativeHandle(ref, () => ({
      onSave,
    }));

    const onSave = async (isTemp: boolean) => {
      const currentForm = formRef.current?.getFormdata();
      // 폼에서 가져온 값이 공백("")일 경우를 대비해 확실히 체크
      const noIssue =
        currentForm?.no_issue && currentForm.no_issue !== ""
          ? currentForm.no_issue
          : formData?.no_issue;

      const confirm1 = await showDialog("confirm", "Due date 확인하셨나요?");
      if (!confirm1) return;

      if (!currentForm.cd_menu) {
        const confirm2 = await showDialog("confirm", "Menu 선택 확인하셨나요?");
        if (!confirm2) return;
      }

      if (!body || body === "") {
        await showDialog("alter", "본문 내용이 없습니다.");
        return;
      }

      const insertData: any = {
        cd_firm: userInfo.cd_firm,
        no_issue: noIssue,
        dt_issue: currentForm.dt_issue,
        cd_emp: currentForm.cd_emp,
        dc_title: currentForm.dc_title,
        fg_item: currentForm.fg_item,
        st_issue: currentForm.st_issue,
        cd_user_reg: userInfo.cd_user,
      };

      // js 데이터 추가
      const js_txt_body = getTxtBody(body);
      const js_rel = getRelIssue(currentForm);
      const js_emp = getEmp(currentForm);
      const js_tag = getTag(currentForm);
      const js_progress = getProgress(currentForm);
      const js_etc_info = getEtcInfo(currentForm);

      insertData.js_txt_body = js_txt_body;
      if (js_rel) insertData.js_rel = js_rel;
      if (js_emp) insertData.js_emp = js_emp;
      if (js_tag) insertData.js_tag = js_tag;
      insertData.js_progress = js_progress;
      insertData.js_etc_info = js_etc_info;

      const dt = currentForm.dt_due;
      insertData.dt_due = dt
        ? `${dt.substring(0, 4)}-${dt.substring(4, 6)}-${dt.substring(6, 8)} 00:00:00.000`
        : null;
      if (formData.tp_issue1) insertData.tp_issue1 = currentForm.tp_issue1;
      if (formData.tp_issue2) insertData.tp_issue2 = currentForm.tp_issue2;
      if (formData.fg_priority)
        insertData.fg_priority = currentForm.fg_priority;

      if (currentForm.dc_recipient) {
        insertData.st_issue = "2"; // assign
        if (js_progress) js_progress[0].tm_assign = getCurrentTime();
        insertData.js_progress = js_progress;

        insertData.arr_cd_emp_assign = currentForm.dc_recipient
          ? currentForm.dc_recipient
              .split(",")
              .map((v: string) => v.trim())
              .filter((v: string) => v !== "")
          : [];
      } else {
        insertData.st_issue = "1"; // open
      }

      if (isTemp) {
        insertData.st_issue = "0";
        if (js_progress) js_progress[0].tm_assign = null;
        insertData.js_progress = js_progress;
      }

      const saveData = {
        sfa_issue: {
          insert: [insertData],
        },
      };

      try {
        await fnSave("SN_SFA_ISSUE_REP_001_btnNewIssue", saveData);

        // temp가 아니라면 텔레그램 연동
        await sendMessage(insertData);

        return true;
      } catch (err) {
        return false;
      }
    };

    const sendMessage = async (snapshotSource?: any) => {
      // 1. 폼의 현재 데이터를 가져오되, 안전하게 빈 객체로 초기화합니다.
      const data = formRef.current?.getFormdata() || {};

      // 2. snapshotData 구성: 저장 시 사용했던 snapshotSource(insertData)를 덮어씌워
      // 누락된 no_issue 등이 확실히 포함되게 합니다.
      const snapshotData = {
        ...data,
        ...(snapshotSource || {}),
        js_txt_body: snapshotSource?.js_txt_body || data?.js_txt_body || [],
        js_attach: snapshotSource?.js_attach || data?.js_attach || [],
      };

      // 3. 전송 파라미터 구성
      const params = {
        ...data, // 기본 폼 데이터
        cd_firm: userInfo.cd_firm,
        msg_flag: "new",
        cd_menu: data?.cd_menu || Menuprops.cd_menu,
        // [중요] 저장에 성공한 번호(snapshotSource.no_issue)를 가장 먼저 참조합니다.
        no_issue: snapshotSource?.no_issue || data?.no_issue,
        snapshot_data: snapshotData,
      };
      // 2026-03-09: const result = callApi("telegram/sendMsg", params);
      await sendIssueSnapshotTelegramByParams(params);
    };

    const onValueChanged = (data: any, param: string, formData: any) => {
      switch (param) {
        case "cd_menu":
          return {
            nm_menu: data.nm_menu,
            cd_menu: data.cd_menu,
            nm_emp_design: data.nm_emp_design,
            nm_emp_dev: data.nm_emp_dev,
          };
        case "tp_issue2":
          let dt_due;
          if (data === "3") {
            // 오류수정(bugfix)
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);

            dt_due = `${tomorrow.getFullYear()}${(tomorrow.getMonth() + 1)
              .toString()
              .padStart(
                2,
                "0"
              )}${tomorrow.getDate().toString().padStart(2, "0")}`;
          } else {
            // 오늘 기준으로 다다음주 월요일 계산
            const today = new Date();
            const day = today.getDay(); // 0 (일) ~ 6 (토)
            const diffToMonday = (day === 0 ? -6 : 1) - day;
            const thisWeekMonday = new Date(today);
            thisWeekMonday.setDate(today.getDate() + diffToMonday);

            const twoWeeksLaterMonday = new Date(thisWeekMonday);
            twoWeeksLaterMonday.setDate(thisWeekMonday.getDate() + 14);

            const yyyy = twoWeeksLaterMonday.getFullYear();
            const mm = String(twoWeeksLaterMonday.getMonth() + 1).padStart(
              2,
              "0"
            );
            const dd = String(twoWeeksLaterMonday.getDate()).padStart(2, "0");

            dt_due = `${yyyy}${mm}${dd}`;
          }

          return {
            dt_due: dt_due,
          };
      }
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <div style={{ textAlign: "right", marginBottom: 10 }}>
          <SnSearchBtn
            text="임시저장"
            onClick={async () => {
              await onSave(true);
              modalRef.current.handleConfirm(false);
            }}
            sx={{ width: 80, marginTop: 0 }}
            spinner={false}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <FreeForm
            ref={formRef}
            data={formData}
            controls={formControlData}
            onValueChanged={onValueChanged}
          />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              marginTop: 10,
              overflow: "hidden",
            }}
          >
            {/* 에디터 */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                border: "1px solid rgb(186,191,199)",
                borderRadius: 4,
              }}
            >
              {formData?.no_issue && (
                <SnEditor
                  uniqueId={uniqueId}
                  selectedData={formData.no_issue}
                  body={body}
                  setBody={setBody}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default SN_SFA_ISSUE_REP_001_POP_001;
